import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, MessageSquareText } from "lucide-react";
import { useModal } from "../contexts/ModalContext";

const API_URL = process.env.REACT_APP_API_URL;

const AgentsModal = () => {
    const navigate = useNavigate();
    const { showAgents, closeAgentsModal } = useModal();
    const [agents, setAgents] = useState([]);
    const [agentsLoading, setAgentsLoading] = useState(false);
    const [agentsError, setAgentsError] = useState("");

    useEffect(() => {
        if (!showAgents) return;
        let isMounted = true;
        async function loadAgents() {
            try {
                setAgentsLoading(true);
                setAgentsError("");
                const res = await fetch(`${API_URL}/api/resellers`);
                const data = await res.json();
                const list = Array.isArray(data) ? data : (data?.resellers || []);
                if (!isMounted) return;
                setAgents(list);
            } catch (e) {
                if (!isMounted) return;
                setAgentsError("Failed to load agents. Please try again.");
            } finally {
                if (isMounted) setAgentsLoading(false);
            }
        }
        loadAgents();
        return () => { isMounted = false; };
    }, [showAgents]);

    if (!showAgents) return null;

    return ReactDOM.createPortal(
        <>
            {/* Dark Backdrop */}
            <div
                className="fixed inset-0 bg-black bg-opacity-50 z-[9998] backdrop-blur-sm"
                onClick={closeAgentsModal}
            />

            {/* Modal Container - Centered */}
            <div className="fixed inset-0 z-[9999] overflow-y-auto pointer-events-none">
                <div className="min-h-screen px-4 flex items-center justify-center py-12">
                    <div
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] overflow-hidden pointer-events-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-4 flex justify-between items-center sticky top-0 z-10">
                            <h2 className="text-2xl font-bold text-white">Find Agents (Runners)</h2>
                            <button
                                onClick={closeAgentsModal}
                                className="p-2 hover:bg-white/20 rounded-full transition-colors group"
                                aria-label="Close modal"
                            >
                                <X className="w-6 h-6 text-white group-hover:scale-110 transition-transform" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 overflow-y-auto max-h-[calc(80vh-72px)] bg-gray-50">
                            {agentsLoading && (
                                <div className="text-center py-12">
                                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
                                    <p className="mt-4 text-gray-600">Loading agents...</p>
                                </div>
                            )}

                            {agentsError && (
                                <div className="text-center py-12">
                                    <p className="text-red-600 font-semibold">{agentsError}</p>
                                    <button
                                        onClick={closeAgentsModal}
                                        className="mt-4 px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                                    >
                                        Close
                                    </button>
                                </div>
                            )}

                            {!agentsLoading && !agentsError && agents.length === 0 && (
                                <div className="text-center py-12">
                                    <p className="text-gray-600">No agents available at the moment.</p>
                                </div>
                            )}

                            {!agentsLoading && !agentsError && agents.length > 0 && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {agents.map((agent) => (
                                        <div
                                            key={agent._id || agent.id}
                                            className="p-5 border-2 border-gray-200 rounded-xl hover:border-orange-400 hover:shadow-lg transition-all duration-200 bg-white"
                                        >
                                            <div className="flex justify-between items-start mb-3">
                                                <div>
                                                    <h3 className="text-lg font-bold text-gray-900">{agent.name}</h3>
                                                    <p className="text-sm text-gray-600">
                                                        {agent.location ? (agent.location.city || agent.location) : 'Location N/A'}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex flex-col space-y-3">
                                                <button
                                                    onClick={() => {
                                                        navigate(`/review/${agent._id || agent.id}`);
                                                        closeAgentsModal();
                                                    }}
                                                    className="w-full flex items-center justify-center space-x-2 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold rounded-xl hover:shadow-lg transition-all transform hover:scale-[1.02] active:scale-[0.98]"
                                                >
                                                    <MessageSquareText className="w-5 h-5" />
                                                    <span>Review Runner</span>
                                                </button>

                                                {agent.status === 'inactive' && (
                                                    <div className="text-center py-1 bg-red-100 text-red-700 text-xs font-bold rounded-full">
                                                        Currently Unavailable
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

export default AgentsModal;
